import { clamp } from 'lodash';
import { getColPositions, getRowPositions } from '../layout/util';
import { getColIndex, getRowIndex } from './util';
import { BlocksViewNode, EditPreview } from '../types';
import { getBlockGrid } from '../layout/blockGrid';
import { Bounds, Vector } from '@ardoq/graph';
import { interp } from '../misc/math';
import { rectIntersects } from '../misc/geometry';
import { NodeHit } from './interactions';

const isRoutingChannel = (channel: number) => (channel & 1) === 0;

/* verify that the node can be placed into the specified cell */
const verifyCell = (node: BlocksViewNode, cell: Bounds) => {
  const blockGrid = getBlockGrid(node.parent!);

  for (let c = cell[0]; c <= cell[2]; ++c) {
    for (let r = cell[1]; r <= cell[3]; ++r) {
      const block = blockGrid.get(c, r);

      if (block && block !== node) {
        return false;
      }
    }
  }

  return true;
};

/* create a "do nothing" edit preview */
export const getNopPreview = (node: BlocksViewNode) => {
  const ret: EditPreview = {
    node: node,
    isGood: true,
    insert: [NaN, NaN],
    posn: [node.col, node.row],
    span: [node.colSpan, node.rowSpan],
    bounds: [node.bounds[0], node.bounds[1], node.bounds[2], node.bounds[3]],
    handles: [node.bounds[0], node.bounds[1], node.bounds[2], node.bounds[3]],
    drop: null,
    real: [[], []],
    fake: [[], []],
  };

  return ret;
};

/* create a "move" edit preview from the specified parameters */
export const getMovePreview = (
  node: BlocksViewNode,
  _hit: NodeHit,
  pointerDown: Vector,
  pointer: Vector,
  resolution: number
) => {
  const ret = getNopPreview(node);

  const group = node.parent!;
  const cols = getColPositions(group);
  const rows = getRowPositions(group);
  const delta = [pointer[0] - pointerDown[0], pointer[1] - pointerDown[1]]; // world move

  ret.handles = [
    cols[node.col] + delta[0],
    rows[node.row] + delta[1],
    cols[node.col + node.colSpan] + delta[0],
    rows[node.row + node.rowSpan] + delta[1],
  ];
  ret.bounds = ret.handles;
  ret.drop = [node.bounds[0], node.bounds[1], node.bounds[2], node.bounds[3]];

  // const center = rectCenter(ret.bounds); // base things on where the center of the node is
  const center = pointer; // base things on where the center of the node is
  const col = clamp(getColIndex(group, center[0]), 0, cols.length - 2);
  const row = clamp(getRowIndex(group, center[1]), 0, rows.length - 2);

  const cell: Bounds = [
    node.col,
    node.row,
    node.col + node.colSpan - 1,
    node.row + node.rowSpan - 1,
  ];

  const slop = 10;

  if (Math.abs(delta[1] / resolution) < slop) {
    ret.real[1].push(rows[node.row]);
    ret.real[1].push(rows[node.row + node.rowSpan]);
  } else {
    ret.posn[1] = row;
    ret.span[1] = 1;

    cell[1] = row;
    cell[3] = row;

    if (isRoutingChannel(row)) {
      ret.insert[1] = row;

      const top = interp(0.15, rows[row], rows[row + 1]);
      const bottom = interp(0.85, rows[row], rows[row + 1]);

      ret.fake[1].push(top);
      ret.fake[1].push(bottom);

      ret.drop[1] = top;
      ret.drop[3] = bottom;
    } else {
      const top = rows[row];
      const bottom = rows[row + 1];

      ret.real[1].push(top);
      ret.real[1].push(bottom);

      ret.drop[1] = top;
      ret.drop[3] = bottom;
    }
  }

  if (Math.abs(delta[0] / resolution) < slop) {
    ret.real[0].push(cols[node.col]);
    ret.real[0].push(cols[node.col + node.colSpan]);
  } else {
    ret.posn[0] = col;
    ret.span[0] = 1;

    cell[0] = col;
    cell[2] = col;

    ret.drop = ret.drop ?? [
      node.bounds[0],
      node.bounds[1],
      node.bounds[2],
      node.bounds[3],
    ];

    if (isRoutingChannel(col)) {
      ret.insert[0] = col;

      const left = interp(0.15, cols[col], cols[col + 1]);
      const right = interp(0.85, cols[col], cols[col + 1]);

      ret.fake[0].push(left);
      ret.fake[0].push(right);

      ret.drop[0] = left;
      ret.drop[2] = right;
    } else {
      const left = cols[col];
      const right = cols[col + 1];

      ret.real[0].push(left);
      ret.real[0].push(right);

      ret.drop[0] = left;
      ret.drop[2] = right;
    }
  }

  if (!(rectIntersects(group.bounds, ret.handles) || !group.parent)) {
    ret.isGood = false;
    ret.real = [[], []];
    ret.fake = [[], []];
  }

  ret.isGood = ret.isGood && verifyCell(node, cell);

  if (!ret.isGood) {
    ret.drop = null;
  }

  return ret;
};

/* create a "resize" edit preview from the specified parameters */
export const getResizePreview = (
  node: BlocksViewNode,
  hit: NodeHit,
  pointerDown: Vector,
  pointer: Vector,
  _resolution: number
) => {
  const ret = getNopPreview(node);
  const group = node.parent;
  const delta = [pointer[0] - pointerDown[0], pointer[1] - pointerDown[1]];

  ret.handles = [
    node.bounds[0] + (hit & NodeHit.Left ? delta[0] : 0),
    node.bounds[1] + (hit & NodeHit.Top ? delta[1] : 0),
    node.bounds[2] + (hit & NodeHit.Right ? delta[0] : 0),
    node.bounds[3] + (hit & NodeHit.Bottom ? delta[1] : 0),
  ];

  if (!group?.children || group.children.length < 2) {
    return ret;
  }

  const cell: Bounds = [
    node.col,
    node.row,
    node.col + node.colSpan - 1,
    node.row + node.rowSpan - 1,
  ];

  const rightBottom = (hit: NodeHit) => {
    const dimension = hit & NodeHit.Right ? 0 : hit & NodeHit.Bottom ? 1 : NaN;

    if (isNaN(dimension)) {
      return;
    }

    const positions =
      dimension === 0 ? getColPositions(group) : getRowPositions(group);

    let channel =
      dimension === 0
        ? getColIndex(group, pointer[dimension])
        : getRowIndex(group, pointer[dimension]);

    channel = clamp(channel, ret.posn[dimension], positions.length - 2);

    const sz = positions[channel + 1] - positions[channel];
    const p = (pointer[dimension] - positions[channel]) / sz;

    if (isRoutingChannel(channel) && p < 0.25) {
      const c = channel; // c is this routing channel

      ret.span[dimension] = c - ret.posn[dimension];
      ret.real[dimension].push(positions[c]);
      ret.bounds[2 + dimension] = positions[c];
      cell[2 + dimension] = c; // range to check for bad
    }

    if (!isRoutingChannel(channel) && p >= 0.75) {
      const c = channel + 1; // c is the routing channel

      ret.span[dimension] = c - ret.posn[dimension];
      ret.real[dimension].push(positions[c]);
      ret.bounds[2 + dimension] = positions[c];
      cell[2 + dimension] = c; // range to check for bad
    }

    if (isRoutingChannel(channel) && p >= 0.25) {
      const c = channel; // c is this routing channel

      ret.insert[dimension] = c;
      ret.span[dimension] = c - ret.posn[dimension] + 1;
      ret.fake[dimension].push(positions[c]);
      ret.fake[dimension].push(positions[c + 1]);
      ret.bounds[2 + dimension] = 0.5 * (positions[c] + positions[c + 1]);
      cell[2 + dimension] = c; // range to check for bad
    }

    if (!isRoutingChannel(channel) && p < 0.25) {
      const c = channel;

      ret.insert[dimension] = c;
      ret.span[dimension] = c - ret.posn[dimension] + 1;
      ret.fake[dimension].push(positions[c]);
      ret.fake[dimension].push(positions[c + 1]);
      ret.bounds[2 + dimension] = 0.5 * (positions[c] + positions[c + 1]);
      cell[2 + dimension] = c; // range to check for bad
    }

    if (!isRoutingChannel(channel) && p >= 0.25 && p < 0.75) {
      const c = channel;

      ret.insert[dimension] = c;
      ret.span[dimension] = c - ret.posn[dimension] + 1;
      ret.fake[dimension].push(positions[c]);
      ret.fake[dimension].push(positions[c + 1]);
      ret.bounds[2 + dimension] = 0.5 * (positions[c] + positions[c + 1]);
      cell[2 + dimension] = c; // range to check for bad
    }
  };

  const leftTop = (hit: NodeHit) => {
    const dimension = hit & NodeHit.Left ? 0 : hit & NodeHit.Top ? 1 : NaN;

    if (isNaN(dimension)) {
      return;
    }

    const positions =
      dimension === 0 ? getColPositions(group) : getRowPositions(group);

    let channel =
      dimension === 0
        ? getColIndex(group, pointer[dimension])
        : getRowIndex(group, pointer[dimension]);

    channel = clamp(channel, 0, ret.posn[dimension] + ret.span[dimension] - 1);

    const sz = positions[channel + 1] - positions[channel];
    const p = (pointer[dimension] - positions[channel]) / sz;

    if (isRoutingChannel(channel) && p < 0.5) {
      const c = channel; // split routing channel

      ret.insert[dimension] = c;
      ret.span[dimension] = ret.posn[dimension] + ret.span[dimension] - c + 1;
      ret.posn[dimension] = c + 1;

      ret.fake[dimension].push(positions[c]);
      ret.fake[dimension].push(positions[c + 1]);
      ret.bounds[dimension] = 0.5 * (positions[c] + positions[c + 1]);
      cell[dimension] = c; // range to check for bad
    }

    if (isRoutingChannel(channel) && p >= 0.5) {
      const c = channel + 1; // snap to start of node channel above

      ret.span[dimension] = ret.posn[dimension] + ret.span[dimension] - c;
      ret.posn[dimension] = c;
      ret.real[dimension].push(positions[c]);
      ret.bounds[dimension] = positions[c];
      cell[dimension] = c; // range to check for bad
    }

    if (!isRoutingChannel(channel) && p < 0.5) {
      const c = channel; // snap down to start of node channel

      ret.span[dimension] = ret.posn[dimension] + ret.span[dimension] - c;
      ret.posn[dimension] = c;
      ret.real[dimension].push(positions[c]);
      ret.bounds[dimension] = positions[c];
      cell[dimension] = c; // range to check for bad
    }

    if (!isRoutingChannel(channel) && p >= 0.5) {
      const c = channel; // split node channel

      ret.insert[dimension] = c;
      ret.span[dimension] = ret.posn[dimension] + ret.span[dimension] - c;
      ret.posn[dimension] = c + 2;
      ret.fake[dimension].push(positions[c]);
      ret.fake[dimension].push(positions[c + 1]);
      ret.bounds[dimension] = 0.5 * (positions[c] + positions[c + 1]);
      cell[dimension] = c; // range to check for bad
    }
  };

  rightBottom(hit & NodeHit.Right);
  rightBottom(hit & NodeHit.Bottom);
  leftTop(hit & NodeHit.Left);
  leftTop(hit & NodeHit.Top);

  ret.isGood = ret.isGood && verifyCell(node, cell);

  return ret;
};
